import axiosInstance from '@/api/axiosInstance';
import type { Product } from '@/types/products';

export interface RoomAnalysis {
  analysis: {
    items: Array<{
      bbox: number[];
      name: string;
      confidence: number;
    }>;
    colors: {
      accent: { hex: string; name: string };
      dominant: { hex: string; name: string };
      secondary: { hex: string; name: string };
    };
    room_area?: {
      width_ft: number;
      height_ft: number;
    };
    room_type?: string;
  };
  imageUrl?: string;
  id?: string;
}

export const analyzeRoom = async (file: File): Promise<RoomAnalysis> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post('/rooms/analyze', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export interface UploadHistoryResponse {
  items: Array<{
    id: number;
    imageUrl: string;
    resizedImageUrl: string;
    roomType: string;
    roomStyle: string;
    isPublic: boolean;
    createdAt: string;
    updatedAt: string;
    analysis: {
      items: Array<{
        bbox: number[];
        name: string;
        id: string;
      }>;
      colors: {
        accent: { hex: string; name: string };
        dominant: { hex: string; name: string };
        secondary: { hex: string; name: string };
      };
      room_area: {
        width_ft: number;
        height_ft: number;
      };
      room_type: string;
      room_style: string;
    };
  }>;
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface ARGenerationResponse {
  generatedImage: string;
  company: {
    id: number;
    name: string;
    description: string;
    avatar: string;
    city: string;
    country: string;
    website: string;
    businessType: string;
    branches: Array<{
      id: number;
      name: string;
      address: string;
      phoneNumber: string;
      imageUrls: string[];
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
  };
  room: {
    id: number;
    analysis: Record<string, unknown>;
    roomType: string;
    roomStyle: string;
    roomArea: {
      width_ft: number;
      height_ft: number;
    };
    imageUrl: string;
    resizedImageUrl: string;
  };
  product: {
    id: number;
    name: string;
    image_urls: string[];
    description: string;
    color: string;
  };
  id: string;
  createdAt: string;
  updatedAt: string;
}

export const generateARView = async (
  roomId: number | string,
  productId: number | string
): Promise<ARGenerationResponse> => {
  const response = await axiosInstance.post(`/analysis`, {
    roomId,
    productId,
  });
  return response.data;
};

export const getUploadHistory = async (
  page = 1,
  limit = 10
): Promise<UploadHistoryResponse> => {
  const response = await axiosInstance.get('/rooms', {
    params: {
      page,
      limit,
    },
  });
  return response.data;
};

export const getAnalysisById = async (
  id: string
): Promise<ARGenerationResponse> => {
  const response = await axiosInstance.get(`/analysis/${id}`);
  return response.data;
};

export const getRoomProducts = async (
  roomId: string | number,
  params?: { query?: string; limit?: number }
): Promise<Product[]> => {
  const response = await axiosInstance.get(`/rooms/${roomId}/products`, {
    params: {
      ...(params?.query && { query: params.query }),
      ...(params?.limit && { limit: params.limit }),
    },
  });
  // API might return array directly or wrapped in { data: [...] }
  return Array.isArray(response.data)
    ? response.data
    : response.data?.data || [];
};
